import { IEngagementData } from '../../facets/LoggerFacets';
import { IItemCache } from '../IItemCacheObserver';
import { graft } from '../../utilities/Graft';
import { itemEngagementControl } from '../../controls/itemEngagementControl';
import { fileNameFacet, folderFacet } from '../../facets/ItemFacets';
import { IActionProps } from '../../components/action/Action';
import { readItems } from '../../operations/readItems';

/**
 * Gets populated engagement data for a single item, using the provided engagement data as a base.
 * @public
 */
export function getItemEngagement<TEngagement extends Partial<IEngagementData> = {}>(
  itemCache: IItemCache,
  props: {
    itemKey: string | undefined;
    engagement?: TEngagement;
  }
): Partial<IEngagementData> & TEngagement {
  const {
    engagement = {},
    itemKey
  } = props;

  const controlHandler = itemCache.demandItemFacet(itemEngagementControl, itemKey);

  return evaluateEngagement(itemCache, {
    itemKey,
    engagement,
    controlHandler
  }) as Partial<IEngagementData> & TEngagement;
}

/**
 * Get engagement logging configuration for an action, which will access the data for the given item once executed.
 * @public
 */
export function getItemActionEngagement(
  itemCache: IItemCache,
  props: {
    itemKey: string | undefined;
    engagement: IEngagementData;
  }
): Pick<IActionProps, 'engagement' | 'getEngagement'> {
  const {
    engagement,
    itemKey
  } = props;

  const controlHandler = itemCache.demandItemFacet(itemEngagementControl, itemKey);

  const { dispatch } = itemCache;

  return {
    engagement,
    getEngagement: async () => {
      return dispatch(readItems((itemCache2: IItemCache) => {
        return evaluateEngagement(itemCache2, {
          itemKey,
          controlHandler,
          engagement
        });
      }));
    }
  };
}

function evaluateEngagement(itemCache: IItemCache, props: {
  itemKey: string | undefined;
  engagement: Partial<IEngagementData>;
  controlHandler: typeof itemEngagementControl.type | undefined;
}): Partial<IEngagementData> {
  const {
    itemKey,
    engagement,
    controlHandler
  } = props;

  const folder = itemCache.demandItemFacet(folderFacet, itemKey);

  // We only need to demand fileNameFacet for non-folder item
  // and should only demand it when it is resolved to avoid auto-trigger a single item fetch.
  const fileName = !folder && itemCache.isItemFacetResolved(fileNameFacet, itemKey) ?
    itemCache.demandItemFacet(fileNameFacet, itemKey) : undefined;

  const itemEngagement: Partial<IEngagementData> = {
    ...(fileName || folder ? {
      objectType: folder ? 'Folder' : 'File'
    } : {}),
    extraData: {
      ...(fileName ? {
        extension: fileName.extension
      } : {})
    }
  };

  const {
    engagement: finalEngagement
  } = itemEngagementControl.evaluate(controlHandler)(itemCache, {
    itemKey,
    engagement: graft(engagement, itemEngagement)
  });

  return finalEngagement;
}
